import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Card, CardContent, Grid,
  TextField, LinearProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, Tooltip, MenuItem,
} from '@mui/material';
import { EventRepeat, PlayArrow, Check, Close, Visibility } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  preview: { color: 'warning', label: '👁 Preview' },
  applied: { color: 'success', label: '✅ Applied' },
  reversed:{ color: 'default', label: '↩️ Reversed' },
};

const YEAR = new Date().getFullYear();

interface Run { id: string; fromYear: number; toYear: number; status: string; totalEmployees: number; totalDaysCarried: number; appliedAt?: string; notes?: string; initiatedBy?: { firstName: string; lastName: string }; createdAt?: string; }
interface PreviewItem { employeeId: string; employeeName: string; department: string; leaveTypeName: string; closingBalance: number; daysCarried: number; expiryDate: string; }

export default function LeaveCarryover() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [runs, setRuns] = useState<Run[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [livePreview, setLivePreview] = useState<PreviewItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [config, setConfig] = useState({ fromYear: YEAR - 1, maxCarryDays: 5, expiryMonths: 3, notes: '' });
  const [runDlg, setRunDlg] = useState(false);
  const [runItemsDlg, setRunItemsDlg] = useState<{ run: Run; items: PreviewItem[] } | null>(null);
  const [applyConfirm, setApplyConfirm] = useState<Run | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [r, st] = await Promise.all([
        apiFetch('/api/leave-carryover/runs'),
        apiFetch(`/api/leave-carryover/stats?year=${YEAR}`),
      ]);
      setRuns(r || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const items = await apiFetch(`/api/leave-carryover/preview?fromYear=${config.fromYear}&maxCarryDays=${config.maxCarryDays}&expiryMonths=${config.expiryMonths}`);
      setLivePreview(items || []);
    } catch (e: any) { setError(e.message); }
    setPreviewLoading(false);
  }, [config.fromYear, config.maxCarryDays, config.expiryMonths]);

  useEffect(() => { if (runDlg) loadPreview(); }, [runDlg, loadPreview]);

  const createRun = async () => {
    try {
      await apiFetch('/api/leave-carryover/runs', { method: 'POST', body: JSON.stringify(config) });
      setRunDlg(false); load();
    } catch (e: any) { setError(e.message); }
  };

  const viewRunItems = async (run: Run) => {
    const items = await apiFetch(`/api/leave-carryover/runs/${run.id}`);
    setRunItemsDlg({ run, items: items || [] });
  };

  const applyRun = async () => {
    if (!applyConfirm) return;
    try {
      await apiFetch(`/api/leave-carryover/runs/${applyConfirm.id}/apply`, { method: 'PATCH' });
      setApplyConfirm(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const totalCarry = livePreview.reduce((s, i) => s + i.daysCarried, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventRepeat sx={{ fontSize: 32, color: '#1565c0' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Leave Carryover Management</Typography>
            <Typography variant="body2" color="text.secondary">Year-end carryover of unused leave balances to next year</Typography>
          </Box>
        </Box>
        {isHR && <Button variant="contained" startIcon={<PlayArrow />} onClick={() => setRunDlg(true)}>Run Carryover</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: `${YEAR} Employees w/ Carryover`, value: stats.totalEmployeesWithCarryover, color: '#1565c0' },
            { label: 'Total Days Carried',   value: `${stats.totalCarryoverDays}d`, color: '#2e7d32' },
            { label: 'Active Carryovers',    value: stats.activeCarryovers,   color: '#e65100' },
            { label: 'Expired Carryovers',   value: stats.expiredCarryovers,  color: '#c62828' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Runs table */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Carryover Runs</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><b>Period</b></TableCell>
              <TableCell><b>Employees</b></TableCell>
              <TableCell><b>Days Carried</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Initiated By</b></TableCell>
              <TableCell><b>Applied At</b></TableCell>
              <TableCell><b>Notes</b></TableCell>
              {isHR && <TableCell><b>Actions</b></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map(r => (
              <TableRow key={r.id} hover>
                <TableCell><Typography fontWeight={700}>{r.fromYear} → {r.toYear}</Typography></TableCell>
                <TableCell>{r.totalEmployees}</TableCell>
                <TableCell><Typography color="primary" fontWeight={700}>{r.totalDaysCarried}d</Typography></TableCell>
                <TableCell><Chip label={STATUS_CFG[r.status]?.label} size="small" color={STATUS_CFG[r.status]?.color} /></TableCell>
                <TableCell>{r.initiatedBy ? `${r.initiatedBy.firstName} ${r.initiatedBy.lastName}` : '—'}</TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{r.appliedAt ? new Date(r.appliedAt).toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</Typography></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary" sx={{ maxWidth: 120, display: 'block' }} noWrap>{r.notes || '—'}</Typography></TableCell>
                {isHR && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Preview"><IconButton size="small" onClick={() => viewRunItems(r)}><Visibility fontSize="small" /></IconButton></Tooltip>
                      {r.status === 'preview' && (
                        <Tooltip title="Apply to leave_balances">
                          <IconButton size="small" color="success" onClick={() => setApplyConfirm(r)}><Check fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {runs.length === 0 && !loading && (
              <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>No carryover runs yet. Create one above.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── RUN DIALOG ── */}
      <Dialog open={runDlg} onClose={() => setRunDlg(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Run Leave Carryover
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setRunDlg(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="From Year" select fullWidth size="small" value={config.fromYear}
                onChange={e => setConfig(p => ({ ...p, fromYear: Number(e.target.value) }))}>
                {[YEAR - 2, YEAR - 1, YEAR].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Max Carry Days" type="number" fullWidth size="small"
                value={config.maxCarryDays} inputProps={{ min: 1, max: 30 }}
                onChange={e => setConfig(p => ({ ...p, maxCarryDays: Number(e.target.value) }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Expiry (months)" type="number" fullWidth size="small"
                value={config.expiryMonths} inputProps={{ min: 1, max: 12 }}
                onChange={e => setConfig(p => ({ ...p, expiryMonths: Number(e.target.value) }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button fullWidth variant="outlined" size="small" onClick={loadPreview} sx={{ height: 40 }}>Refresh Preview</Button>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Notes (optional)" fullWidth size="small" value={config.notes}
                onChange={e => setConfig(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>

          {previewLoading && <LinearProgress sx={{ mb: 1 }} />}

          {livePreview.length > 0 && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#e8f5e9' }}>
              <Typography variant="body2" fontWeight={700} color="#2e7d32">
                📋 Preview: {livePreview.length} employees · {totalCarry} total days to carry forward → expiry: {livePreview[0]?.expiryDate}
              </Typography>
            </Paper>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Dept</b></TableCell>
                  <TableCell><b>Leave Type</b></TableCell>
                  <TableCell align="right"><b>Closing Balance</b></TableCell>
                  <TableCell align="right"><b>Days Carried</b></TableCell>
                  <TableCell><b>Expiry</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {livePreview.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>{item.employeeName}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{item.department}</Typography></TableCell>
                    <TableCell>{item.leaveTypeName}</TableCell>
                    <TableCell align="right">{item.closingBalance}d</TableCell>
                    <TableCell align="right"><Typography fontWeight={700} color="primary">{item.daysCarried}d</Typography></TableCell>
                    <TableCell><Typography variant="caption">{item.expiryDate}</Typography></TableCell>
                  </TableRow>
                ))}
                {livePreview.length === 0 && !previewLoading && (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No eligible balances for carryover (check leave type allowCarryOver flag).</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDlg(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={createRun} disabled={livePreview.length === 0}>
            Save as Preview Run
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VIEW RUN ITEMS DIALOG ── */}
      <Dialog open={!!runItemsDlg} onClose={() => setRunItemsDlg(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {runItemsDlg?.run.fromYear} → {runItemsDlg?.run.toYear} Carryover Preview
          <Chip label={STATUS_CFG[runItemsDlg?.run.status ?? '']?.label} size="small" color={STATUS_CFG[runItemsDlg?.run.status ?? '']?.color} sx={{ ml: 1 }} />
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setRunItemsDlg(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Employee</b></TableCell><TableCell><b>Dept</b></TableCell>
                  <TableCell><b>Leave Type</b></TableCell><TableCell align="right"><b>Balance</b></TableCell>
                  <TableCell align="right"><b>Carried</b></TableCell><TableCell><b>Expiry</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(runItemsDlg?.items ?? []).map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>{item.employeeName}</TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{item.department}</Typography></TableCell>
                    <TableCell>{item.leaveTypeName}</TableCell>
                    <TableCell align="right">{item.closingBalance}d</TableCell>
                    <TableCell align="right"><Typography fontWeight={700} color="primary">{item.daysCarried}d</Typography></TableCell>
                    <TableCell>{item.expiryDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          {isHR && runItemsDlg?.run.status === 'preview' && (
            <Button variant="contained" color="success" onClick={() => { setApplyConfirm(runItemsDlg!.run); setRunItemsDlg(null); }}>Apply This Run</Button>
          )}
          <Button onClick={() => setRunItemsDlg(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── APPLY CONFIRM ── */}
      <Dialog open={!!applyConfirm} onClose={() => setApplyConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>⚠️ Apply Carryover</DialogTitle>
        <DialogContent dividers>
          <Typography>
            This will write <b>{applyConfirm?.totalDaysCarried} days</b> of carryover balance to <b>{applyConfirm?.totalEmployees} employees'</b> leave balances for {applyConfirm?.toYear}. This cannot be automatically reversed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={applyRun}>Confirm & Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
